<template>
  <div v-if="misClientes" class="area-comercial-page bg-white text-primary">
    <div class="layout area-comercial-layout d-flex align-items-start gap-20">
      <aside class="sidebar area-comercial-sidebar">
        <TabsComponent
          :tabs="areaTabs"
          :initial-tab="activeTab"
          :mode="TABS_MODE.SCROLL"
          :layout="TABS_LAYOUT.VERTICAL"
          @tab-changed="handleTabChange" />
      </aside>

      <div class="contenido area-comercial-content">
        <div class="contenedor area-comercial-container">
          <div class="mb-35">
            <div class="d-flex align-items-center pointer mb-10" @click="doVolver">
              <CustomIcon :icon="leftIcon" size="16px" class="text-primary flex-shrink-0 me-2" />
              <span class="text-primary semibold h6 mb-0">
                {{ $t('staff.misClientes.areaComercial') }}
              </span>
            </div>
            <span class="p text-primary">
              {{ $t('staff.misClientes.subtitulo') }}
            </span>
          </div>

          <section id="section-catalogo" class="section-content mb-35">
            <div class="h6 semibold mb-15">
              {{ $t('staff.misClientes.linkCatalogo') }}
            </div>
            <div class="catalogo-caja p-20 rounded-12">
              <p class="p text-primary mb-15">
                {{ $t('staff.misClientes.linkCatalogoDescripcion') }}
              </p>
              <div class="d-flex align-items-center catalogo-link-row">
                <div class="catalogo-url p flex-grow-1 min-w-0">{{ catalogoUrl }}</div>
                <MyGocuButton
                  primary
                  class="flex-shrink-0 d-none d-md-inline-flex"
                  :label="$t('staff.misClientes.copiarLink')"
                  :suffix-icon="copiarIcon"
                  @click="copyToClipboard(catalogoUrl)" />
                <MyGocuButton
                  primary
                  class="flex-shrink-0 d-md-none"
                  :prefix-icon="copiarIcon"
                  @click="copyToClipboard(catalogoUrl)" />
              </div>
            </div>
          </section>

          <section id="section-otros-links" class="section-content mb-35">
            <div
              class="d-flex align-items-center mb-15"
              :class="{ pointer: hasExperiencias }"
              @click="otrosLinksOpen = !otrosLinksOpen">
              <CustomIcon
                v-if="hasExperiencias"
                :icon="otrosLinksOpen ? plegarIcon : desplegarIcon"
                size="16px"
                class="text-primary flex-shrink-0 me-2" />
              <span class="h6 semibold mb-0">{{ $t('staff.misClientes.otrosLinks') }}</span>
            </div>

            <SinResultados
              v-if="!hasExperiencias"
              class="p-0"
              :icon="linkIcon"
              :titulo="$t('staff.misClientes.sinExperiencias')"
              :descripcion="$t('staff.misClientes.sinExperienciasDescripcion')" />
            <template v-else-if="otrosLinksOpen">
              <InputBuscar
                v-model="busquedaLinks"
                class="mb-15"
                :placeholder="$t('staff.misClientes.buscarPlaceholder')" />

              <SinResultados v-if="!agrupacionesFiltradas.length && !experienciasFiltradas.length" class="p-0" />
              <div v-else class="links-lista rounded-12" :class="{ 'links-lista-simple': !agrupaciones.length }">
                <div v-for="item in agrupacionesFiltradas" :key="item.uri" class="links-grupo">
                  <div class="links-header links-header-agrupacion p-15 pointer" @click="toggleAgrupacion(item.uri)">
                    <div class="links-agrupacion-main">
                      <div
                        class="links-thumb-header flex-shrink-0"
                        :style="{ backgroundImage: `url('${item.imagen || agenciaStore.getImagenFondo}')` }" />
                      <div class="links-header-info flex-grow-1 min-w-0 pr-15">
                        <MarqueeText class="h6 semibold text-primary p-m">{{ item.nombre }}</MarqueeText>
                        <div v-if="item.fechaInicio || item.lugar" class="d-flex align-items-center flex-nowrap">
                          <div v-if="item.fechaInicio" class="d-flex align-items-center flex-shrink-0">
                            <CustomIcon
                              :icon="calendarioIcon"
                              size="16px"
                              size-mobile="12px"
                              class="text-primary flex-shrink-0 me-2" />
                            <span class="small sub-m text-primary">
                              {{ formatLinksDateRange(item.fechaInicio, item.fechaFin) }}
                            </span>
                          </div>
                          <div v-if="item.fechaInicio && item.lugar" class="links-separator mx-25" />
                          <div v-if="item.lugar" class="d-flex align-items-center min-w-0">
                            <CustomIcon
                              :icon="ubicacionIcon"
                              size="16px"
                              size-mobile="12px"
                              class="text-primary flex-shrink-0 me-2" />
                            <MarqueeText class="small sub-m text-primary">{{ item.lugar }}</MarqueeText>
                          </div>
                        </div>
                      </div>
                      <div
                        v-if="item.experiencias.length"
                        class="links-chevron flex-shrink-0 d-flex align-items-center">
                        <CustomIcon
                          :icon="agrupacionesCerradas[item.uri] ? desplegarIcon : plegarIcon"
                          size="16px"
                          class="text-primary" />
                      </div>
                    </div>
                    <div class="links-agrupacion-actions">
                      <div class="links-header-ventas col-md-1 flex-shrink-0" @click.stop>
                        <div class="d-none d-md-block">
                          <div class="p semibold text-primary text-end">{{ item.numeroVentas }}</div>
                          <div class="sub medium text-primary text-end">{{ $t('staff.misClientes.ventasLabel') }}</div>
                        </div>
                        <div class="d-md-none d-flex align-items-baseline">
                          <span class="p semibold text-primary">{{ item.numeroVentas }}</span>
                          <span class="sub text-primary ms-1">{{ $t('staff.misClientes.ventasLabel') }}</span>
                        </div>
                      </div>
                      <div class="links-header-copiar col-md-4 d-flex justify-content-end">
                        <MyGocuButton
                          secondary
                          class="flex-shrink-0"
                          :label="$t('staff.misClientes.copiarLinkAgrupacion')"
                          :suffix-icon="copiarIcon"
                          @click="copyToClipboard(getUrlAgrupacion(item))" />
                      </div>
                    </div>
                  </div>

                  <div v-if="item.experiencias.length && !agrupacionesCerradas[item.uri]">
                    <div
                      v-for="experiencia in item.experiencias"
                      :key="experiencia.viajeId"
                      class="links-fila-experiencia">
                      <div
                        class="links-thumb-fila flex-shrink-0"
                        :style="{
                          backgroundImage: `url('${experiencia.imagen || agenciaStore.getImagenFondo}')`,
                        }" />
                      <div class="links-fila-info flex-grow-1 min-w-0">
                        <MarqueeText class="p medium text-primary mb-1">{{ experiencia.nombre }}</MarqueeText>
                        <div class="d-flex align-items-center flex-nowrap">
                          <div class="d-flex align-items-center flex-shrink-0">
                            <CustomIcon
                              :icon="calendarioIcon"
                              size="14px"
                              size-mobile="12px"
                              class="text-primary flex-shrink-0 me-2" />
                            <span class="small sub-m text-primary">
                              {{ formatLinksDateRange(experiencia.fechaInicio, experiencia.fechaFin) }}
                            </span>
                          </div>
                          <div class="links-separator mx-15" />
                          <div class="d-flex align-items-center min-w-0">
                            <CustomIcon
                              :icon="ubicacionIcon"
                              size="14px"
                              size-mobile="12px"
                              class="text-primary flex-shrink-0 me-2" />
                            <MarqueeText class="small sub-m text-primary">{{ experiencia.lugar }}</MarqueeText>
                          </div>
                        </div>
                      </div>
                      <div class="col-md-1 d-none d-md-block text-end">
                        <div class="p semibold text-primary">{{ experiencia.numeroVentas }}</div>
                        <div v-if="!agrupaciones.length" class="sub medium text-primary">
                          {{ $t('staff.misClientes.ventasLabel') }}
                        </div>
                      </div>
                      <div class="links-fila-stats col-md-3 d-flex align-items-center flex-shrink-0">
                        <div
                          v-if="experiencia.comision != null"
                          class="links-comision d-none d-md-flex align-items-center ml-30">
                          <span class="sub text-primary links-comision-label">
                            {{ $t('staff.misClientes.comisionLabel') }}
                          </span>
                          <span class="p text-primary ms-1">{{ formatComision(experiencia) }}</span>
                        </div>
                        <div class="d-flex d-md-none align-items-center flex-wrap gap-2">
                          <span class="small bold text-primary">{{ experiencia.numeroVentas }}</span>
                          <span class="sub text-primary">{{ $t('staff.misClientes.ventasLabel') }}</span>
                          <span v-if="experiencia.comision != null" class="p text-primary">
                            {{ $t('staff.misClientes.comision', { n: formatComision(experiencia) }) }}
                          </span>
                        </div>
                      </div>
                      <div class="links-fila-copiar col-md-1 d-flex justify-content-end ml-m-20">
                        <MyGocuButton
                          secondary
                          class="flex-shrink-0"
                          :prefix-icon="copiarIcon"
                          @click="copyToClipboard(getUrlExperiencia(experiencia.viajeId))" />
                      </div>
                    </div>
                  </div>
                </div>

                <div v-if="experienciasFiltradas.length" class="otras-experiencias">
                  <div
                    v-if="agrupaciones.length"
                    class="links-header links-header-otras d-flex align-items-center pointer"
                    @click="otrasExperienciasOpen = !otrasExperienciasOpen">
                    <CustomIcon
                      :icon="otrasExperienciasOpen ? plegarIcon : desplegarIcon"
                      size="16px"
                      class="links-chevron-otras text-primary flex-shrink-0 mr-15" />
                    <span class="h6 semibold text-primary mb-0 flex-grow-1">
                      {{ $t('staff.misClientes.otrasExperiencias') }}
                    </span>
                  </div>

                  <template v-if="otrasExperienciasOpen">
                    <div v-for="item in experienciasFiltradas" :key="item.viajeId" class="links-fila-experiencia">
                      <div
                        class="links-thumb-fila flex-shrink-0"
                        :style="{ backgroundImage: `url('${item.imagen || agenciaStore.getImagenFondo}')` }" />
                      <div class="links-fila-info flex-grow-1 min-w-0">
                        <MarqueeText class="p medium text-primary mb-1">{{ item.nombre }}</MarqueeText>
                        <div class="d-flex align-items-center flex-nowrap">
                          <div class="d-flex align-items-center flex-shrink-0">
                            <CustomIcon
                              :icon="calendarioIcon"
                              size="14px"
                              size-mobile="12px"
                              class="text-primary flex-shrink-0 me-2" />
                            <span class="small sub-m text-primary">
                              {{ formatLinksDateRange(item.fechaInicio, item.fechaFin) }}
                            </span>
                          </div>
                          <div class="links-separator mx-15" />
                          <div class="d-flex align-items-center min-w-0">
                            <CustomIcon
                              :icon="ubicacionIcon"
                              size="14px"
                              size-mobile="12px"
                              class="text-primary flex-shrink-0 me-2" />
                            <MarqueeText class="small sub-m text-primary">{{ item.lugar }}</MarqueeText>
                          </div>
                        </div>
                      </div>
                      <div class="col-md-1 d-none d-md-block text-end">
                        <div class="p semibold text-primary">{{ item.numeroVentas }}</div>
                        <div v-if="!agrupaciones.length" class="sub medium text-primary">
                          {{ $t('staff.misClientes.ventasLabel') }}
                        </div>
                      </div>
                      <div class="links-fila-stats col-md-3 d-flex align-items-center flex-shrink-0">
                        <div
                          v-if="item.comision != null"
                          class="links-comision d-none d-md-flex align-items-center ml-30">
                          <span class="sub text-primary links-comision-label">
                            {{ $t('staff.misClientes.comisionLabel') }}
                          </span>
                          <span class="p text-primary ms-1">{{ formatComision(item) }}</span>
                        </div>
                        <div class="d-flex d-md-none align-items-center flex-wrap gap-2">
                          <span class="small bold text-primary">{{ item.numeroVentas }}</span>
                          <span class="sub text-primary">{{ $t('staff.misClientes.ventasLabel') }}</span>
                          <span v-if="item.comision != null" class="p text-primary">
                            {{ $t('staff.misClientes.comision', { n: formatComision(item) }) }}
                          </span>
                        </div>
                      </div>
                      <div class="links-fila-copiar col-md-1 d-flex justify-content-end ml-m-20">
                        <MyGocuButton
                          secondary
                          class="flex-shrink-0"
                          :prefix-icon="copiarIcon"
                          @click="copyToClipboard(getUrlExperiencia(item.viajeId))" />
                      </div>
                    </div>
                  </template>
                </div>
              </div>
            </template>
          </section>

          <section id="section-ventas" class="section-content">
            <div class="h6 semibold mb-15">
              {{ $t('staff.misClientes.ventas', { n: misClientes.length }) }}
            </div>

            <SinResultados
              v-if="!misClientes.length"
              class="p-0"
              :icon="tarjetaIcon"
              :titulo="$t('staff.misClientes.sinVentas')"
              :descripcion="$t('staff.misClientes.sinVentasDescripcion')" />
            <template v-else>
              <InputBuscar
                v-model="busquedaVentas"
                class="mb-15"
                :placeholder="$t('staff.misClientes.buscarVentasPlaceholder')" />

              <SinResultados v-if="!ventasFiltradas.length" class="p-0" />
              <div v-else class="ventas-tabla rounded-12">
                <div class="ventas-header row gx-0 align-items-center py-10 px-12">
                  <div class="col-2 p medium">{{ $t('staff.misClientes.tabla.fechas') }}</div>
                  <div class="col-2 p medium">{{ $t('home.form.personalInformation.name') }}</div>
                  <div class="col-3 p medium">{{ $t('home.form.personalInformation.apellidos') }}</div>
                  <div class="col-3 p medium">{{ $t('general.experiencia') }}</div>
                  <div class="col-2 p medium text-center">{{ $t('staff.misClientes.tabla.estado') }}</div>
                </div>
                <div
                  v-for="(cliente, index) in ventasFiltradas"
                  :key="index"
                  class="ventas-fila row gx-0 align-items-center p-12">
                  <div class="col-2 p">
                    <div>{{ formatDateDDMMYYYY(cliente.fechaReserva) }}</div>
                    <div>{{ formatTimeHHmm(cliente.fechaReserva) }}</div>
                  </div>
                  <div class="col-2 p min-w-0">
                    <MarqueeText>{{ cliente.nombre }}</MarqueeText>
                  </div>
                  <div class="col-3 p min-w-0">
                    <MarqueeText>{{ cliente.apellidos }}</MarqueeText>
                  </div>
                  <div class="col-3 p min-w-0">
                    <MarqueeText>{{ cliente.experiencia }}</MarqueeText>
                    <div v-if="cliente.fechaInicio">
                      {{ formatDateRangeDDmm(cliente.fechaInicio, cliente.fechaFin) }}
                    </div>
                  </div>
                  <div class="col-2 d-flex justify-content-center">
                    <span class="estado-chip small" :style="estadoChipStyle(cliente.estadoPago)">
                      {{ estadoChipLabel(cliente.estadoPago) }}
                    </span>
                  </div>
                </div>
              </div>
            </template>
          </section>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { computed, onBeforeMount, ref } from 'vue';
  import { useRouter } from 'vue-router';
  import { useI18n } from 'vue-i18n';
  import { createToaster } from '@meforma/vue-toaster';
  import { useUserStore } from 'src/store_pinia/userStore';
  import { useLoadingStore } from 'src/store_pinia/loadingStore';
  import { agenciaStoreModule } from 'src/store_pinia/agencia';
  import StaffApiService from 'src/services/api/StaffApiService';
  import {
    formatDateDDMMYYYY,
    formatDateIntervalDDmmm,
    formatDateRangeDDmm,
    formatTimeHHmm,
  } from 'src/helpers/dateUtiles';
  import { formatPrice } from 'src/helpers/numberUtils';
  import { TIPO_COMISION_VENTA } from 'src/constants/viajeConstants';
  import { copyToClipboard } from 'src/helpers/linkUtils';
  import { TABS_LAYOUT, TABS_MODE } from 'src/helpers/tabsUtils';
  import TabsComponent from 'src/components/tabs/TabsComponent.vue';
  import CustomIcon from 'src/components/CustomIcon.vue';
  import MyGocuButton from 'src/components/common/buttons/MyGocuButton.vue';
  import InputBuscar from 'src/components/inputs/InputBuscar.vue';
  import MarqueeText from 'src/components/common/MarqueeText.vue';
  import SinResultados from 'src/components/misc/SinResultados.vue';
  import { getEstadoReservaChip } from 'src/utils/estadosUtils';
  import leftIcon from 'src/assets/icons/raw/izquierda.svg';
  import copiarIcon from 'src/assets/icons/raw/copiar.svg';
  import plegarIcon from 'src/assets/icons/raw/plegar.svg';
  import desplegarIcon from 'src/assets/icons/raw/desplegar.svg';
  import calendarioIcon from 'src/assets/icons/raw/calendario.svg';
  import ubicacionIcon from 'src/assets/icons/raw/ubicacion.svg';
  import linkIcon from 'src/assets/icons/raw/link.svg';
  import tarjetaIcon from 'src/assets/icons/raw/tarjeta.svg';

  const { t } = useI18n();
  const router = useRouter();
  const loadingStore = useLoadingStore();
  const userStore = useUserStore();
  const agenciaStore = agenciaStoreModule();
  const toaster = createToaster({ position: 'top-right' });

  const misClientes = ref();
  const experiencias = ref([]);
  const agrupaciones = ref([]);
  const activeTab = ref('catalogo');
  const otrosLinksOpen = ref(true);
  const otrasExperienciasOpen = ref(true);
  const busquedaLinks = ref('');
  const busquedaVentas = ref('');
  const agrupacionesCerradas = ref({});

  const areaTabs = computed(() => [
    { id: 'catalogo', label: t('staff.misClientes.linkCatalogo'), active: true },
    { id: 'otros-links', label: t('staff.misClientes.otrosLinks'), active: true },
    { id: 'ventas', label: t('staff.misClientes.ventasTab'), active: true },
  ]);

  const hasExperiencias = computed(() => agrupaciones.value.length > 0 || experiencias.value.length > 0);

  const catalogoUrl = computed(() => `${window.location.origin}/grupos/viajes?comercialId=${userStore.getUsuario.id}`);

  const agrupacionesFiltradas = computed(() => {
    const busqueda = busquedaLinks.value.trim().toLowerCase();
    if (!busqueda) return agrupaciones.value;

    return agrupaciones.value
      .map(item => {
        const hijas = item.experiencias.filter(e => e.nombre.toLowerCase().includes(busqueda));
        if (item.nombre.toLowerCase().includes(busqueda) || hijas.length > 0) {
          return {
            ...item,
            experiencias: item.nombre.toLowerCase().includes(busqueda) ? item.experiencias : hijas,
          };
        }
        return null;
      })
      .filter(Boolean);
  });

  const experienciasFiltradas = computed(() => {
    const busqueda = busquedaLinks.value.trim().toLowerCase();
    if (!busqueda) return experiencias.value;
    return experiencias.value.filter(item => item.nombre.toLowerCase().includes(busqueda));
  });

  const ventasFiltradas = computed(() => {
    const busqueda = busquedaVentas.value.trim().toLowerCase();
    if (!busqueda) return misClientes.value;
    return misClientes.value.filter(cliente => {
      const viajeFechas = formatDateRangeDDmm(cliente.fechaInicio, cliente.fechaFin);
      const texto = [cliente.nombre, cliente.apellidos, cliente.experiencia, viajeFechas, cliente.estadoPago]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return texto.includes(busqueda);
    });
  });

  function toggleAgrupacion(uri) {
    agrupacionesCerradas.value[uri] = !agrupacionesCerradas.value[uri];
  }

  function handleTabChange(tabId) {
    activeTab.value = tabId;
  }

  function doVolver() {
    router.back();
  }

  function formatLinksDateRange(fechaInicio, fechaFin) {
    return formatDateIntervalDDmmm(fechaInicio, fechaFin).toLowerCase();
  }

  function formatComision({ comision, tipoComision }) {
    return tipoComision === TIPO_COMISION_VENTA.FIJO
      ? `${formatPrice(comision)} ${agenciaStore.getCurrency}`
      : `${comision} %`;
  }

  function getUrlExperiencia(viajeId) {
    return `${window.location.origin}/grupos/viajes/${viajeId}?comercialId=${userStore.getUsuario.id}`;
  }

  function getUrlAgrupacion(agrupacion) {
    const query = agrupacion.codigo
      ? `codigo=${encodeURIComponent(agrupacion.codigo)}&comercialId=${userStore.getUsuario.id}`
      : `comercialId=${userStore.getUsuario.id}`;
    return `${window.location.origin}/agrupaciones/${agrupacion.uri}?${query}`;
  }

  function estadoChipStyle(estado) {
    const chip = getEstadoReservaChip(estado);
    return {
      backgroundColor: chip.bgColor ?? 'transparent',
      color: chip.textColor ?? 'inherit',
    };
  }

  function estadoChipLabel(estado) {
    const { labelKey } = getEstadoReservaChip(estado);
    return labelKey ? t(labelKey) : estado || '';
  }

  async function init() {
    try {
      loadingStore.show();
      const [clientes, experienciasRes, agrupacionesRes] = await Promise.all([
        StaffApiService.getMisClientes(),
        StaffApiService.getMisExperiencias(),
        StaffApiService.getMisAgrupaciones(),
      ]);
      misClientes.value = clientes;
      experiencias.value = experienciasRes;
      agrupaciones.value = agrupacionesRes;
    } catch (error) {
      console.error(error);
      toaster.error(t('errors.cargaInformacion'));
    } finally {
      loadingStore.hide();
    }
  }

  onBeforeMount(async () => await init());
</script>

<style lang="scss" scoped>
  .area-comercial-page {
    min-height: 80vh;
    width: 100%;
    max-width: 1200px;
    margin: 0 auto;
    padding: 12rem 0 4rem;
    box-sizing: border-box;
  }

  .area-comercial-sidebar {
    width: 290px;
    flex-shrink: 0;
    position: sticky;
    top: 120px;
    align-self: flex-start;
  }

  .area-comercial-content {
    flex: 1;
    max-width: 890px;
    min-width: 0;
    overflow-x: clip;
  }

  .area-comercial-container {
    width: 100%;
    max-width: 100%;
    min-width: 0;
  }

  .section-content {
    scroll-margin-top: 180px;
  }

  .catalogo-caja {
    border: 1px solid rgba(var(--bs-primary-rgb), 0.2);
  }

  .catalogo-link-row {
    background: white;
    box-shadow: inset 0 0 3px rgba(var(--bs-primary-rgb), 0.35);
    border-radius: var(--bs-border-radius-xxl);
    padding: 4px 4px 4px 20px;
    gap: 8px;
  }

  .catalogo-url {
    color: var(--bs-primary);
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    opacity: 0.7;
  }

  .links-lista {
    border: 1px solid rgba(var(--bs-primary-rgb), 0.2);
    overflow: hidden;
  }

  .links-header {
    background-color: rgba(var(--bs-primary-rgb), 0.05);
    border-top: 1px solid rgba(var(--bs-primary-rgb), 0.2);
    border-bottom: 1px solid rgba(var(--bs-primary-rgb), 0.2);
  }

  .links-header-agrupacion {
    display: flex;
    align-items: center;
  }

  .links-agrupacion-main,
  .links-agrupacion-actions {
    display: contents;
  }

  .links-header-agrupacion .links-chevron {
    order: -1;
    margin-right: 15px;
  }

  .links-header-agrupacion .links-header-ventas {
    margin-left: auto;
  }

  .links-lista > .links-grupo:first-child > .links-header {
    border-top: 0;
  }

  .links-header-otras {
    padding: 16px;
  }

  .links-thumb-header {
    width: 50px;
    height: 50px;
    border-radius: 12px;
    background-color: rgba(var(--bs-primary-rgb), 0.08);
    background-size: cover;
    background-position: center;
    margin-right: 15px;
  }

  .links-thumb-fila {
    width: 40px;
    height: 40px;
    border-radius: 12px;
    background-color: rgba(var(--bs-primary-rgb), 0.08);
    background-size: cover;
    background-position: center;
    margin-right: 15px;
  }

  .links-separator {
    width: 1px;
    height: 16px;
    background-color: rgba(var(--bs-primary-rgb), 0.2);
    flex-shrink: 0;
  }

  .links-fila-experiencia {
    display: flex;
    align-items: center;
    padding: 10px 15px;
    border-top: 1px solid rgba(var(--bs-primary-rgb), 0.2);
  }

  .links-fila-experiencia .links-thumb-fila {
    margin-left: 37px;
  }

  .links-lista-simple .links-fila-experiencia .links-thumb-fila {
    margin-left: 0;
  }

  .links-lista-simple .links-fila-experiencia:first-child {
    border-top: 0;
  }

  .links-header + div > .links-fila-experiencia:first-child,
  .links-header + .links-fila-experiencia {
    border-top: 0;
  }

  .links-comision-label {
    opacity: 0.8;
  }

  .ventas-tabla {
    width: 100%;
    border: 1px solid rgba(var(--bs-primary-rgb), 0.1);
    overflow: hidden;
    color: var(--bs-primary);
  }

  .ventas-header {
    background-color: rgba(var(--bs-primary-rgb), 0.05);
  }

  .ventas-fila {
    border-top: 1px solid rgba(var(--bs-primary-rgb), 0.1);
  }

  .estado-chip {
    display: inline-block;
    padding: 2px 6px;
    border-radius: 10px;
    white-space: nowrap;
  }

  @media screen and (max-width: 768px) {
    .area-comercial-page {
      max-width: 100%;
      margin: 0;
      padding: 12vh 15px 2rem;
      overflow-x: clip;
      box-sizing: border-box;
    }

    .area-comercial-layout {
      display: block;
    }

    .area-comercial-sidebar {
      display: none;
    }

    .area-comercial-content {
      width: 100%;
    }

    .catalogo-link-row {
      flex-wrap: nowrap;
      padding: 4px 4px 4px 16px;
    }

    .links-header-agrupacion {
      flex-direction: column;
      align-items: stretch;
      gap: 12px;
    }

    .links-agrupacion-main {
      display: flex;
      align-items: center;
      min-width: 0;
      width: 100%;
      gap: 12px;
    }

    .links-agrupacion-actions {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
    }

    .links-header-agrupacion .links-thumb-header {
      margin-right: 0;
    }

    .links-header-agrupacion .links-header-info {
      flex: 1;
      min-width: 0;
      padding-right: 0;
    }

    .links-header-agrupacion .links-chevron {
      order: 0;
      margin-right: 0;
      margin-left: auto;
    }

    .links-header-agrupacion .links-header-ventas {
      margin-left: 0;
    }

    .links-header-agrupacion .links-header-copiar {
      margin-left: 0;
    }

    .links-header-otras .links-chevron-otras {
      order: 2;
      margin-right: 0;
      margin-left: 8px;
    }

    .links-fila-experiencia {
      display: grid;
      grid-template-columns: auto 1fr auto;
      grid-template-areas:
        'thumb info copy'
        'thumb stats copy';
      align-items: center;
      column-gap: 12px;
      row-gap: 4px;
      padding: 12px 15px;
    }

    .links-fila-experiencia .links-thumb-fila {
      grid-area: thumb;
      align-self: start;
      margin-right: 0;
      margin-left: 0;
    }

    .links-fila-experiencia .links-fila-info {
      grid-area: info;
      min-width: 0;
    }

    .links-fila-experiencia .links-fila-stats {
      grid-area: stats;
    }

    .links-fila-experiencia .links-fila-copiar {
      grid-area: copy;
      align-self: center;
      margin-left: 0;
    }

    .links-header-agrupacion .links-separator {
      margin-left: 10px;
      margin-right: 10px;
    }

    .ventas-tabla {
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
    }

    .ventas-header,
    .ventas-fila {
      min-width: 700px;
    }
  }
</style>
